<script setup lang="ts">
import { NPopconfirm, NTooltip } from 'naive-ui'
import type { Reminder } from '../stores/reminders'
import { reminderClockTime } from '../composables/reminderDayGroups'

// One one-time reminder in the Reminders card: a done checkbox, the
// message with its delivery state underneath, the fire time in mono, and
// edit / delete. Schedule-generated reminders can't be edited here — the
// schedule owns their text.
defineProps<{ reminder: Reminder }>()

const emit = defineEmits<{
  toggle: [id: number, done: boolean]
  edit: [id: number]
  delete: [id: number]
}>()

const CONFIRM_TRUNCATE_LENGTH = 50

function truncated(text: string): string {
  return text.length > CONFIRM_TRUNCATE_LENGTH ? text.slice(0, CONFIRM_TRUNCATE_LENGTH) + '…' : text
}

function onChange(event: Event, id: number): void {
  emit('toggle', id, (event.target as HTMLInputElement).checked)
}

const ACTION_CLASS =
  'min-h-11 rounded-lg border border-border-control px-2.5 text-xs text-ink-muted hover:text-ink sm:min-h-8'
</script>

<template>
  <div class="flex min-h-[52px] items-center gap-3 border-t border-border-divider py-1.5" data-testid="reminder-row">
    <label class="-m-3 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        class="m-0 h-[18px] w-[18px] cursor-pointer accent-life"
        :checked="reminder.done"
        :aria-label="`Mark reminder as done: ${reminder.message}`"
        @change="onChange($event, reminder.id)"
      />
    </label>
    <span class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span class="break-words" :class="reminder.done ? 'text-ink-faint line-through' : 'text-ink'">{{
        reminder.message
      }}</span>
      <span class="text-xs text-ink-faint">
        <span v-if="reminder.fired_at !== null"><span aria-hidden="true">✓ </span>sent</span>
        <span v-else>pending</span>
        <NTooltip v-if="reminder.reminder_schedule_id" placement="top">
          <template #trigger>
            <span class="cursor-help" tabindex="0"> · from schedule</span>
          </template>
          Generated by a recurring schedule — edit the schedule to change this.
        </NTooltip>
      </span>
    </span>
    <span class="shrink-0 font-mono text-xs text-ink-muted">{{ reminderClockTime(reminder.fire_at) }}</span>
    <button
      v-if="!reminder.reminder_schedule_id"
      type="button"
      :class="ACTION_CLASS"
      :aria-label="`Edit reminder: ${reminder.message}`"
      @click="emit('edit', reminder.id)"
    >
      Edit
    </button>
    <NPopconfirm positive-text="Delete" negative-text="Cancel" @positive-click="emit('delete', reminder.id)">
      <template #trigger>
        <button type="button" :class="ACTION_CLASS" :aria-label="`Delete reminder: ${reminder.message}`">Delete</button>
      </template>
      Delete reminder "{{ truncated(reminder.message) }}"?
    </NPopconfirm>
  </div>
</template>
